// Steuerpaket S4: PDF aus dem freigegebenen Stand (Büro und Eigentümerportal), auf dem Briefpapier.
import { jsPDF } from 'jspdf';
import { LAYOUT, pdfText } from '../dokumente/blankoBrief';
import type { StandGruppe, SteuerpaketStand } from './steuerpaketStand';

const eurPdf = (n: number) => `${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
const datum = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '-');

export function erzeugeSteuerpaketPdf(stand: SteuerpaketStand, hintergrund: string | null): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const L = LAYOUT; const links = L.links; const rechts = L.rechts;
  const papier = () => { if (hintergrund) pdf.addImage(hintergrund, 'JPEG', 0, 0, 210, 297, 'briefpapier', 'FAST'); };
  let y = 46;
  const platz = (mm: number) => { if (y + mm > L.ende) { pdf.addPage(); papier(); y = L.folgeseiteStart; } };
  const text = (t: string, x: number, opt?: { fett?: boolean; groesse?: number; grau?: boolean; rechts?: boolean }) => {
    pdf.setFont('helvetica', opt?.fett ? 'bold' : 'normal'); pdf.setFontSize(opt?.groesse ?? 10);
    pdf.setTextColor(opt?.grau ? 110 : 30, opt?.grau ? 110 : 30, opt?.grau ? 110 : 30);
    pdf.text(pdfText(t), x, y, opt?.rechts ? { align: 'right' } : undefined);
  };
  const zeile = (l: string, betrag: number | null, opt?: { fett?: boolean; einzug?: number; grau?: boolean }) => {
    platz(5.5);
    const breite = rechts - links - (opt?.einzug ?? 0) - 35;
    const teile = pdf.splitTextToSize(pdfText(l), breite) as string[];
    teile.forEach((t, i) => { text(t, links + (opt?.einzug ?? 0), { fett: opt?.fett, grau: opt?.grau, groesse: opt?.einzug ? 9 : 10 }); if (i < teile.length - 1) y += 4.5; });
    if (betrag != null) text(eurPdf(betrag), rechts, { fett: opt?.fett, grau: opt?.grau, rechts: true, groesse: opt?.einzug ? 9 : 10 });
    y += opt?.einzug ? 4.6 : 5.6;
  };
  const linie = () => { pdf.setDrawColor(200, 200, 200); pdf.line(links, y - 3.5, rechts, y - 3.5); };
  const gruppenListe = (gs: readonly StandGruppe[]) => { for (const g of gs) { zeile(g.text, g.betrag, { fett: true }); for (const k of g.konten) zeile(k.konto, k.betrag, { einzug: 5, grau: true }); } };

  papier();
  text(`Steuerpaket ${stand.jahr}`, links, { fett: true, groesse: 16 }); y += 7;
  text(`Einkünfte aus Vermietung und Verpachtung (Anlage V) – ${stand.eigentuemer}`, links, { groesse: 11 }); y += 7;
  // Pflichthinweis (Björn, Regel 8) – gut sichtbar in einem Kasten
  pdf.setFillColor(245, 240, 228); pdf.roundedRect(links, y - 4.5, rechts - links, 15, 2, 2, 'F');
  text(stand.hinweis, links + 4, { fett: true }); y += 5;
  text('Grundlage sind die tatsächlichen Zahlungen (Zahlungsdatum, 10-Tage-Regel zum Jahreswechsel).', links + 4, { groesse: 9, grau: true }); y += 11;

  for (const o of stand.objekte) {
    platz(30);
    text(o.titel, links, { fett: true, groesse: 12 }); text(o.umfang, rechts, { grau: true, groesse: 9, rechts: true }); y += 7;
    if (o.art === 'mv') {
      text('Einnahmen', links, { fett: true, grau: true, groesse: 9 }); y += 5;
      gruppenListe(o.einnahmen);
      linie(); zeile('Summe Einnahmen', o.summeEinnahmen, { fett: true }); y += 2;
    }
    text('Werbungskosten (ohne AfA)', links, { fett: true, grau: true, groesse: 9 }); y += 5;
    gruppenListe(o.werbungskosten);
    linie(); zeile('Summe Werbungskosten', o.summeWerbungskosten, { fett: true });
    if (o.art === 'mv') { zeile('Überschuss (vor AfA und eigenen Ergänzungen)', o.summeEinnahmen - o.summeWerbungskosten, { fett: true }); }
    for (const h of o.hinweise) zeile(h, null, { einzug: 0, grau: true });
    if (o.nichtAnlageV.length) {
      y += 1; zeile('Nicht enthalten (keine Einkünfte aus Vermietung):', null, { grau: true });
      for (const n of o.nichtAnlageV) zeile(n.text, n.betrag, { einzug: 5, grau: true });
    }
    zeile(`Stand der Buchhaltung: ${datum(o.buchhaltungBis)}`, null, { grau: true });
    y += 5;
  }
  if (stand.nichtEnthalten.length) { zeile(`Nicht enthalten: ${stand.nichtEnthalten.join('; ')}`, null, { grau: true }); y += 3; }

  platz(12 + stand.ergaenzungen.length * 9);
  text('Von Ihnen bzw. Ihrem Steuerberater zu ergänzen', links, { fett: true, groesse: 12 }); y += 8;
  for (const e of stand.ergaenzungen) {
    platz(9);
    text(e, links, { groesse: 10 });
    pdf.setDrawColor(150, 150, 150); pdf.line(rechts - 40, y + 0.8, rechts, y + 0.8);
    text('EUR', rechts + 2, { groesse: 8, grau: true });
    y += 9;
  }

  const anzahl = pdf.getNumberOfPages();
  for (let s = 1; s <= anzahl; s++) {
    pdf.setPage(s); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(110, 110, 110);
    pdf.text(pdfText(`${stand.hinweis} Erstellt ${datum(stand.erstellt.slice(0, 10))}.`), links, 270);
    if (anzahl > 1) pdf.text(`Seite ${s} von ${anzahl}`, rechts, 270, { align: 'right' });
  }
  return pdf;
}

export const steuerpaketDateiname = (stand: Pick<SteuerpaketStand, 'jahr' | 'eigentuemer'>) =>
  `Steuerpaket ${stand.jahr} ${stand.eigentuemer}`.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() + '.pdf';
